import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { Printer, Download, BoxArrowUpRight, X } from "react-bootstrap-icons";
import { api } from "../api/api";

/**
 * Modal preview for jsreport-rendered PDFs.
 *
 * Flow:
 *   1. On open: fetch the signed URL → fetch PDF bytes → blob → object URL
 *   2. Render the object URL inside an <iframe> (same-origin blob: scheme,
 *      so iframe.contentWindow.print() works)
 *   3. Buttons:
 *      - Print  → iframe.contentWindow.print()
 *      - Download → <a download> on the object URL
 *      - Open in new tab → window.open(signed URL) for users who prefer the
 *        browser's full-window PDF viewer
 *
 * The object URL is revoked on close to free memory.
 *
 * Equivalent of HBS's apps/web/src/components/print-preview.tsx, adapted to
 * v3's react-bootstrap Modal + styled-components stack.
 */
const overlayButtonStyle = {
  border: "1px solid #D4D4D5",
  borderRadius: "6px",
  background: "#00197B",
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 10px",
  margin: "0 4px",
  fontSize: "13px",
};

const iconStyle = { width: "14px", height: "14px" };

export function ModalPdfPreview({
  show,
  onHide,
  pdfPath,
  pdfParams,
  fileName,
}) {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // iOS Safari (and most mobile browsers) can't print a cross-document iframe
  // — `iframe.contentWindow.print()` silently falls back to printing the
  // parent page (the whole modal overlay). On mobile we route Print through
  // window.open() so the native PDF viewer handles printing.
  const isMobile =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!show || !pdfPath) return;
    let revokedUrl = null;
    const ctrl = new AbortController();

    setLoading(true);
    setError(null);

    api
      .getSessionApiUrl(pdfPath, pdfParams)
      .then((url) => {
        setSignedUrl(url);
        return fetch(url, { signal: ctrl.signal });
      })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        revokedUrl = objectUrl;
        setPdfUrl(objectUrl);
      })
      .catch((err) => {
        if (!ctrl.signal.aborted) {
          // eslint-disable-next-line no-console
          console.error("PDF preview load failed:", err);
          setError(err.message || "Failed to load PDF");
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => {
      ctrl.abort();
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
      setPdfUrl(null);
      setSignedUrl(null);
    };
    // We deliberately serialize pdfParams via JSON in the dep array so the
    // effect re-runs when the param values change but not on every render.
  }, [show, pdfPath, JSON.stringify(pdfParams || {})]);

  const handlePrint = () => {
    if (isMobile && signedUrl) {
      // iOS/Android: open the PDF in a new tab so the native PDF viewer's
      // print can target the actual PDF (not the surrounding modal DOM).
      window.open(signedUrl, "_blank");
      return;
    }
    iframeRef.current?.contentWindow?.print();
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${fileName || "specsheet"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenInNewTab = () => {
    if (signedUrl) window.open(signedUrl, "_blank");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="pdf-preview-modal"
      contentClassName="pdf-preview-modal-content"
    >
      <Modal.Header
        style={{
          padding: "8px 12px",
          background: "#f4f4f8",
          alignItems: "center",
        }}
      >
        <Modal.Title style={{ fontSize: "15px", fontWeight: 600 }}>
          {t("Print") || "Print Preview"}
        </Modal.Title>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            style={overlayButtonStyle}
            onClick={handlePrint}
            disabled={!pdfUrl || loading}
          >
            <Printer style={iconStyle} />
            {t("Print") || "Print"}
          </Button>
          <Button
            style={overlayButtonStyle}
            onClick={handleDownload}
            disabled={!pdfUrl || loading}
          >
            <Download style={iconStyle} />
            PDF
          </Button>
          <Button
            style={overlayButtonStyle}
            onClick={handleOpenInNewTab}
            disabled={!signedUrl}
          >
            <BoxArrowUpRight style={iconStyle} />
            {t("Open in new tab") || "Open in new tab"}
          </Button>
          <Button
            style={{
              ...overlayButtonStyle,
              background: "transparent",
              color: "#333",
              padding: "6px 8px",
            }}
            onClick={onHide}
            aria-label="Close"
          >
            <X style={{ width: "18px", height: "18px" }} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body
        style={{ padding: 0, height: "82vh", background: "#525659" }}
      >
        {!pdfPath ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#fff",
              fontSize: "14px",
            }}
          >
            {t("No specsheet available to print") ||
              "No specsheet available to print"}
          </div>
        ) : loading || !pdfUrl ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#fff",
              fontSize: "14px",
            }}
          >
            {error
              ? `${t("Error") || "Error"}: ${error}`
              : t("Loading") || "Loading…"}
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            title="Specsheet PDF Preview"
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              background: "#fff",
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
